import { useState } from "react";
import { Mail, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { CANDIDATE } from "@/data/content";
import { Marked, Asterisk, ArrowDraw } from "@/components/campaign/HandDrawn";

const initial = { name: "", email: "", subject: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message received. Thanks for writing in.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="section border-b border-ink/25">
        <div className="container-x grid gap-12 md:grid-cols-12 items-end">
          <div className="md:col-span-8">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Contact
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl tracking-tight leading-[0.92] text-ink">
              Drop a <Marked>line</Marked>.<br />
              We&apos;ll write back.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink/75 leading-relaxed text-lg">
              Press, partnership, policy questions, or just{" "}
              <span className="italic font-semibold text-terracotta text-base">neighborly hellos</span>{" "}
              — all welcome.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-parchment">
        <div className="container-x grid gap-14 md:grid-cols-12">
          <aside className="md:col-span-4 space-y-8">
            <div className="bg-white/5 border border-rule p-6">
              <p className="overline">Email</p>
              <a
                href={`mailto:${CANDIDATE.email}`}
                className="mt-3 flex items-center gap-2 text-ink font-bold hover:text-terracotta break-all"
                data-testid="contact-email-link"
              >
                <Mail size={16} className="text-terracotta shrink-0" /> {CANDIDATE.email}
              </a>
            </div>
            <div className="bg-white/5 border border-rule p-6">
              <p className="overline">Based in</p>
              <p className="mt-3 inline-flex items-center gap-2 text-ink font-bold">
                <MapPin size={16} className="text-terracotta" /> Manchester, NH — Hillsborough 40
              </p>
            </div>
            <div className="bg-terracotta text-cream border border-rule p-6">
              <p className="overline !text-cream">Press inquiries</p>
              <p className="mt-4 text-cream leading-relaxed">
                Use the form and select &ldquo;Press&rdquo; in the subject line. We aim to respond
                within <span className="italic font-semibold text-cream text-base">24 hours</span>.
              </p>
            </div>
          </aside>

          <form onSubmit={onSubmit} className="md:col-span-8 bg-white/5 border border-rule p-8 space-y-6" data-testid="contact-form">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Your name *" name="name" value={form.name} onChange={handle} testid="contact-name" />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={handle} testid="contact-email" />
            </div>
            <Field label="Subject" name="subject" value={form.subject} onChange={handle} testid="contact-subject" />
            <div>
              <label className="overline" htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows={6}
                value={form.message}
                onChange={handle}
                data-testid="contact-message"
                className="mt-3 w-full border border-rule bg-white/5 px-4 py-3 text-ink focus:outline-none focus:border-terracotta"
              />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                data-testid="contact-submit"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                {loading ? "Sending…" : "Send message"}
              </button>
              <span className="italic font-semibold text-terracotta text-sm">Talk soon</span>
              <ArrowDraw className="h-5 w-12" />
            </div>
            {done && (
              <p className="text-sm font-bold uppercase tracking-wider text-terracotta" data-testid="contact-success">
                Got it — we&apos;ll be in touch.
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testid }) => (
  <div>
    <label className="overline" htmlFor={name}>{label}</label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-3 w-full border border-rule bg-white/5 px-4 py-3 text-ink focus:outline-none focus:border-terracotta"
    />
  </div>
);

export default Contact;
